import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CircleSlash,
  Gauge,
  KeyRound,
  Loader2,
  RefreshCcw,
  Server,
  ShieldAlert,
  Webhook,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  applyReservePartnerCommand,
  fetchReserveAdminIntegrations,
  fetchReserveProductionReadiness,
} from '../lib/forfeoReserveProductionClient';
import type {
  ForfeoReserveAdminIntegrationSnapshot,
  ForfeoReservePartnerApplication,
  ForfeoReserveProductionReadinessSnapshot,
} from '../../supabase/functions/_shared/forfeo-reserve-production';

export default function AdminReserveIntegrations() {
  const [snapshot, setSnapshot] =
    useState<ForfeoReserveAdminIntegrationSnapshot | null>(null);
  const [readiness, setReadiness] =
    useState<ForfeoReserveProductionReadinessSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const applications = useMemo(
    () => snapshot?.applications || [],
    [snapshot],
  );

  async function refresh() {
    setBusy(true);
    setMessage('');
    try {
      const [integrations, production] = await Promise.all([
        fetchReserveAdminIntegrations(),
        fetchReserveProductionReadiness(),
      ]);
      setSnapshot(integrations);
      setReadiness(production);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function setStatus(
    application: ForfeoReservePartnerApplication,
    kind: 'activate_application' | 'suspend_application',
  ) {
    setBusy(true);
    try {
      await applyReservePartnerCommand({
        kind,
        businessId: application.businessId,
        applicationId: application.id,
      });
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-7xl px-4 pb-20 pt-28">
        <header className="rounded-[2rem] bg-gradient-to-br from-slate-950 via-indigo-950 to-blue-900 p-8 text-white shadow-2xl">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-cyan-300">
            Administration Forfeo
          </p>
          <div className="mt-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-3xl font-black">
                Intégrations partenaires Reserve
              </h1>
              <p className="mt-3 max-w-3xl text-sm text-blue-100">
                Applications, domaines, clés, quotas, webhooks, alertes et portes
                de mise en production.
              </p>
            </div>
            <button
              type="button"
              onClick={refresh}
              disabled={busy}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-white/10 px-4 font-black hover:bg-white/20 disabled:opacity-50"
            >
              {busy ? <Loader2 className="animate-spin" /> : <RefreshCcw />}
              Actualiser
            </button>
          </div>
        </header>

        {message && (
          <div className="mt-5 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 font-bold text-amber-900">
            <AlertTriangle />
            {message}
          </div>
        )}

        {snapshot && (
          <section className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Metric
              icon={<Server />}
              label="Applications"
              value={snapshot.totals.applications}
            />
            <Metric
              icon={<KeyRound />}
              label="Clés actives"
              value={snapshot.totals.activeApiKeys}
            />
            <Metric
              icon={<Webhook />}
              label="Webhooks en attente"
              value={snapshot.totals.pendingWebhookDeliveries}
            />
            <Metric
              icon={<ShieldAlert />}
              label="Dead-letter"
              value={snapshot.totals.deadLetterDeliveries}
              warning={snapshot.totals.deadLetterDeliveries > 0}
            />
          </section>
        )}

        {readiness && (
          <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Porte de production
                </p>
                <h2 className="mt-1 text-2xl font-black">
                  {readiness.overallState === 'ready'
                    ? 'Prêt'
                    : readiness.overallState === 'degraded'
                      ? 'Dégradé'
                      : 'Bloqué'}
                </h2>
              </div>
              <span
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-black ${
                  readiness.overallState === 'ready'
                    ? 'bg-emerald-100 text-emerald-800'
                    : readiness.overallState === 'degraded'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-red-100 text-red-800'
                }`}
              >
                <Gauge size={18} />
                {readiness.environment}
              </span>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {readiness.checks.map((check) => (
                <div
                  key={check.kind}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 p-4"
                >
                  {check.state === 'pass' ? (
                    <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-600" />
                  ) : check.state === 'warn' ? (
                    <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" />
                  ) : (
                    <CircleSlash className="mt-0.5 shrink-0 text-red-600" />
                  )}
                  <div>
                    <p className="font-black">{check.kind}</p>
                    <p className="mt-1 text-sm text-slate-500">{check.message}</p>
                  </div>
                </div>
              ))}
            </div>

            {readiness.blockers.length > 0 && (
              <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="font-black text-red-900">Blocages</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-800">
                  {readiness.blockers.map((blocker) => (
                    <li key={blocker}>{blocker}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        <section className="mt-6 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-6">
            <h2 className="text-xl font-black">Applications partenaires</h2>
          </div>
          <div className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
            {applications.map((application) => (
              <article
                key={application.id}
                className="rounded-2xl border border-slate-200 p-5"
              >
                <p className="text-xs font-black uppercase tracking-widest text-slate-400">
                  {application.billingTier}
                </p>
                <h3 className="mt-1 text-lg font-black">{application.name}</h3>
                <p className="mt-2 text-xs text-slate-500">
                  Entreprise {application.businessId.slice(0, 8)} ·{' '}
                  {application.origins.length} domaine(s)
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <p className="text-slate-500">Usage heure</p>
                    <p className="mt-1 font-black">
                      {application.usageHour}/{application.hourlyQuota}
                    </p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <p className="text-slate-500">Usage jour</p>
                    <p className="mt-1 font-black">
                      {application.usageDay}/{application.dailyQuota}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {application.status === 'active' ? (
                    <button
                      type="button"
                      onClick={() => setStatus(application, 'suspend_application')}
                      className="rounded-xl bg-red-50 px-3 py-2 text-xs font-black text-red-700"
                    >
                      Suspendre
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setStatus(application, 'activate_application')}
                      className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700"
                    >
                      Activer
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      applyReservePartnerCommand({
                        kind: 'run_smoke_test',
                        businessId: application.businessId,
                        applicationId: application.id,
                      }).then(refresh)
                    }
                    className="rounded-xl bg-blue-50 px-3 py-2 text-xs font-black text-blue-700"
                  >
                    Smoke test
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
  warning = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  warning?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border bg-white p-5 shadow-sm ${
        warning ? 'border-red-200' : 'border-slate-200'
      }`}
    >
      <div className={warning ? 'text-red-600' : 'text-blue-700'}>{icon}</div>
      <p className="mt-3 text-xs font-black uppercase tracking-widest text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-3xl font-black">{value}</p>
    </div>
  );
}
